import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export type SemanticStateKind = 'status' | 'attention' | 'warning' | 'error' | 'success' | 'loading' | 'empty';

const chipColors: Record<SemanticStateKind, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  status: 'default',
  attention: 'warning',
  warning: 'warning',
  error: 'error',
  success: 'success',
  loading: 'info',
  empty: 'default',
};

export function StatusChip({ label, kind = 'status' }: { label: string; kind?: SemanticStateKind }) {
  return <Chip size="small" label={label} color={chipColors[kind]} aria-label={label} />;
}

export function AttentionIndicator({ count, label = 'Needs review' }: { count?: number; label?: string }) {
  const display = typeof count === 'number' ? `${count} ${label}` : label;
  return <StatusChip label={display} kind="attention" />;
}

export function EmptyState({ title, message, action }: { title: string; message?: string; action?: ReactNode }) {
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ py: 4, px: 2, textAlign: 'center' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{title}</Typography>
      {message && <Typography variant="body2" color="text.secondary">{message}</Typography>}
      {action}
    </Stack>
  );
}

export function ErrorState({ message }: { message: string }) {
  return <Alert severity="error">{message}</Alert>;
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <Box role="status" aria-label={label} aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={18} />
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  );
}
